import { Routes } from '~/constants/routes';
import { RESOURCE_TYPE } from '~/constants/user';
import { hasAnyRole } from '~/utils/roles';
import { getGatewayClient } from '~/services/api/clients';
import { apiGatewayEndpoints } from '~/services/arcane-api-gateway/api-gateway-endpoints';

/**
 * Product analytics for Arcane.
 *
 * THE ONLY MODULE THAT KNOWS THE VENDOR. Everything else calls the functions
 * below and names no product, so swapping PostHog out means rewriting this
 * file and nothing else.
 *
 * Mirrors the privacy posture of rosalind's `useTracking.js` (the same org's
 * already-reviewed PostHog setup): identify only with a backend-issued
 * pseudonym (see `identifyUser`), mask element text/attributes, template URLs
 * before they leave the browser, and keep every PostHog surface beyond core
 * analytics (session replay, remote feature flags, surveys, heatmaps) switched
 * off.
 *
 * Enablement is env-only here (arcane is one tenant per deploy, unlike
 * rosalind's per-tenant config): with `VUE_APP_POSTHOG_PROJECT_ID` or
 * `VUE_APP_POSTHOG_URL` missing, tracking stays inert and every exported
 * function is a safe no-op. Registers under dev (`DEV`: dev/mca/local),
 * staging (`STG`) and production (`PROD`); the stage only sets the
 * `environment` and `environment_slug` the events carry.
 */

// ---------------------------------------------------------------------------
// 1. THE SCRUBBER — runs on every event just before it is sent.
// ---------------------------------------------------------------------------

const UNKNOWN_PATH = '/unknown';

const URL_PROPERTIES = new Set([
  '$current_url',
  '$host',
  '$pathname',
  '$referrer',
  '$referring_domain',
  '$prev_pageview_pathname',
]);

// posthog-js builds `$initial_*` / `$session_entry_*` names by concatenation,
// so they never appear literally in its source — matched by prefix instead.
const URL_PROPERTY_PREFIXES = ['$initial_', '$session_entry_'];

/**
 * Route patterns a captured path is templated against, so `/assist/abc-123`
 * reports as `/assist/:conversationId` rather than leaking a conversation id.
 * Kept in sync by hand with `constants/routes.js` — add a pattern here
 * whenever a new param-bearing route is added there.
 */
const ROUTE_PATTERNS = [
  Routes.Dashboard,
  Routes.Conversation.path,
  Routes.Authoring.path,
  Routes.Callback,
  Routes.Home,
  Routes.Login,
  Routes.Error,
  Routes.MyCreations,
  Routes.UserImages,
];

const patternToRegExp = (pattern) => {
  const source = pattern
    .split('/')
    .filter((segment) => segment !== '')
    .map((segment) => {
      if (segment.startsWith(':')) {
        return segment.endsWith('?') ? '(?:/[^/]+)?' : '/[^/]+';
      }
      return `/${segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`;
    })
    .join('');

  return new RegExp(`^${source || '/'}/?$`);
};

const patternToTemplate = (pattern) => pattern.replace(/\?/g, '');

let routeMatchers = null;

const getRouteMatchers = () => {
  if (!routeMatchers) {
    routeMatchers = ROUTE_PATTERNS.map((pattern) => ({
      match: patternToRegExp(pattern),
      template: patternToTemplate(pattern),
    }));
  }
  return routeMatchers;
};

/** Replace a URL with the route template it corresponds to, or UNKNOWN_PATH. */
export const sanitizeUrl = (rawUrl) => {
  if (typeof rawUrl !== 'string' || rawUrl === '') {
    return UNKNOWN_PATH;
  }

  let pathname;
  try {
    pathname = new URL(rawUrl, 'http://localhost').pathname;
  } catch {
    return UNKNOWN_PATH;
  }

  const matched = getRouteMatchers().find(({ match }) => match.test(pathname));
  return matched ? matched.template : UNKNOWN_PATH;
};

const isUrlProperty = (key) =>
  URL_PROPERTIES.has(key) || URL_PROPERTY_PREFIXES.some((prefix) => key.startsWith(prefix));

/**
 * Attributes autocapture copies off the clicked element as `attr__<name>`.
 * Repeated here (in addition to `element_attribute_ignorelist` on init) so
 * the scrub still holds if that option is ever changed.
 */
const BLOCKED_ELEMENT_ATTRIBUTES = new Set([
  'attr__aria-label',
  'attr__title',
  'attr__alt',
  'attr__placeholder',
  'attr__href',
  'attr__value',
  'attr__name',
  'attr__src',
  'attr__srcset',
  // Not a standard HTML attribute — it's Vue's `v-model` fallthrough onto a
  // component's root element when the component doesn't declare a
  // `modelValue` prop (found leaking a reference's title text this way from
  // a stray `v-model` on EditableReferenceField, since fixed at the source
  // in CustomReferenceListItem.vue — kept here in case the same mistake
  // happens again elsewhere).
  'attr__modelvalue',
]);

export const sanitizeElements = (elements) => {
  if (!Array.isArray(elements)) {
    return elements;
  }

  return elements.map((element) =>
    Object.fromEntries(
      Object.entries(element ?? {}).filter(([key]) => key !== '$el_text' && !BLOCKED_ELEMENT_ATTRIBUTES.has(key)),
    ),
  );
};

// `$set`/`$set_once` carry their own copy of person properties, including
// URL-derived ones — verified against rosalind's live data. Rewriting only
// `event.properties` leaves a raw URL behind on the person record.
const PERSON_PROPERTY_CARRIERS = ['$set', '$set_once'];

const sanitizeUrlProperties = (properties) =>
  Object.fromEntries(
    Object.entries(properties).map(([key, value]) => {
      if (PERSON_PROPERTY_CARRIERS.includes(key) && value && typeof value === 'object') {
        return [key, sanitizeUrlProperties(value)];
      }
      return isUrlProperty(key) && typeof value === 'string' ? [key, sanitizeUrl(value)] : [key, value];
    }),
  );

const sanitizeEventLevelPersonProperties = (event) => {
  PERSON_PROPERTY_CARRIERS.forEach((carrier) => {
    if (event[carrier] && typeof event[carrier] === 'object') {
      event[carrier] = sanitizeUrlProperties(event[carrier]);
    }
  });
};

/**
 * What our own named events (added via `trackEvent`) are allowed to carry.
 * An allowlist, not a denylist, so a careless future call site drops its
 * property instead of shipping it. Extend alongside each new event — never
 * the error message/description, which routinely quotes client content.
 */
const EVENT_PROPERTIES = new Set([
  'outcome',
  'error_code',
  'area',
  'conversation_id',
  'conversation_type',
  'filter_applied',
  'duration_bucket',
  'tool_count',
]);

/**
 * Super properties registered once via `posthog.register` (see
 * `registerGlobalProperties`/`registerRoles`) and expected on every event —
 * including our own named ones. Without this set, `allowlistProperties`
 * would strip them from custom events the same way it strips everything
 * else not explicitly named, even though they ride on `$`-prefixed events
 * for free. `product` in particular is load-bearing: this PostHog project
 * is shared with other products, so a custom event without it can't be told
 * apart from another product's event of the same name.
 */
const SCHEMA_PROPERTIES = new Set([
  'product',
  'environment',
  'environment_slug',
  'account_id',
  'user_role',
  'surface',
  'app_version',
]);

const TRANSPORT_PROPERTIES = new Set([
  'token',
  'distinct_id',
  '$device_id',
  '$anon_distinct_id',
  '$process_person_profile',
  '$session_id',
  '$window_id',
  '$insert_id',
  '$time',
  '$lib',
  '$lib_version',
  '$config_defaults',
]);

export const allowlistProperties = (event) => {
  if (!event) {
    return null;
  }

  event.properties = Object.fromEntries(
    Object.entries(event.properties ?? {}).filter(
      ([key]) => TRANSPORT_PROPERTIES.has(key) || SCHEMA_PROPERTIES.has(key) || EVENT_PROPERTIES.has(key),
    ),
  );

  return event;
};

/** The `before_send` hook: the last thing that runs before an event leaves the browser. */
export const sanitizeEvent = (event) => {
  if (!event) {
    return null;
  }

  sanitizeEventLevelPersonProperties(event);

  // Our own events declare what they carry, so they get the strict
  // allowlist rather than URL templating.
  if (typeof event.event === 'string' && !event.event.startsWith('$')) {
    return allowlistProperties(event);
  }

  const properties = sanitizeUrlProperties(event.properties ?? {});

  if (Array.isArray(properties.$elements)) {
    properties.$elements = sanitizeElements(properties.$elements);
  }

  event.properties = properties;
  return event;
};

// ---------------------------------------------------------------------------
// 2. THE CLIENT
// ---------------------------------------------------------------------------

/**
 * posthog-js is tens of KB gzipped and would otherwise sit in the
 * render-blocking entry chunk, so it's dynamically imported instead of
 * statically — the SDK downloads in parallel with first render rather than
 * ahead of it. Nothing below touches `posthog` before `initialized` is true,
 * and `startIfEnabled` defers itself until the module has landed, so the
 * public API stays synchronous and every entry point keeps no-opping until
 * tracking has actually started.
 */
let posthog = null;
const posthogReady = import('posthog-js').then(
  (module) => {
    posthog = module.default;
  },
  (error) => {
    console.warn('[tracking] could not load the analytics SDK; continuing without it', error);
  },
);

let initialized = false;
let identifyGeneration = 0;
// `identifyUser` is called from a route guard, so it runs on every protected
// navigation, not just once at login — skip the rehash when the email hasn't
// changed rather than re-identifying (and re-hashing) on every route change.
let identifiedEmail = null;
// Tracked separately from `identifiedEmail`: the route guard's `roles` can
// start incomplete and grow once `authStore/FetchUserData` resolves (it reads
// org-feature-derived roles from the profile endpoint, not just the JWT
// claim), so registration must not be skipped just because the email hasn't
// changed.
let registeredRolesKey = null;
// Unknown until the first `identifyUser` call after login; `isEnabled()`
// treats "unknown" the same as "not Anthill's org" so tracking stays inert
// until proven otherwise, rather than assuming yes.
let currentRoles = [];
// The latest area view reported before tracking started (the landing route runs
// before the SDK has loaded and `identifyUser` has supplied the roles), replayed
// once init completes so the first screen of a session isn't lost.
let pendingAreaView = null;

const getToken = () => import.meta.env.VUE_APP_POSTHOG_PROJECT_ID?.trim();
const getHost = () => import.meta.env.VUE_APP_POSTHOG_URL?.trim();

/** The org subdomain, the same way `plugins/authClient.js` reads it. */
const getOrgSlug = () => {
  const host = window.location.host.split('.')[0];
  return ['dev', 'staging', 'arcane', 'localhost:3000'].includes(host) ? null : host;
};

/** STAGE → the envelope's environment value, matching the backend contract. */
const getEnvironment = () => {
  const stage = import.meta.env.VUE_APP_STAGE;
  if (stage === 'PROD') return 'production';
  if (stage === 'STG') return 'sandbox';
  return 'testing';
};

/** The concrete deployment slug. */
const getEnvironmentSlug = () => {
  const stage = import.meta.env.VUE_APP_STAGE;
  if (stage === 'PROD') return 'arcane';
  if (stage === 'STG') return 'arcane-staging';
  return 'arcane-dev';
};

const APP_VERSION = import.meta.env.VUE_APP_APP_VERSION?.trim();

// The active organization's name, once the profile has loaded. Until then
// (and on hosts without an org subdomain) `account_id` falls back to the
// subdomain slug.
let accountName = null;

const registerGlobalProperties = () => {
  const accountId = accountName ?? getOrgSlug();

  posthog.register({
    product: 'Arcane',
    surface: 'frontend',
    environment: getEnvironment(),
    environment_slug: getEnvironmentSlug(),
    ...(APP_VERSION ? { app_version: APP_VERSION } : {}),
    ...(accountId ? { account_id: accountId } : {}),
  });
};

const ENABLED_STAGES = new Set(['DEV', 'STG', 'PROD']);

/**
 * Org-level allowlist, same shape as `useSecondOrderReferencesGating`:
 * true only for orgs carrying `feature:posthog_tracking` (Anthill's own
 * tenants, not customers). ANDed into `isEnabled()` below rather than
 * checked after the fact, so `posthog.init()` never runs at all for an
 * org that doesn't have it — mirrors how rosalind ANDs its
 * backend-delivered `features.posthog.enabled` into its own `isEnabled()`.
 */
const isAllowedOrgTracking = (roles) => hasAnyRole(roles, `feature:${RESOURCE_TYPE.POSTHOG_TRACKING}`);

const isEnabled = () =>
  Boolean(
    ENABLED_STAGES.has(import.meta.env.VUE_APP_STAGE) &&
      isAllowedOrgTracking(currentRoles) &&
      getToken() &&
      getHost(),
  );

/**
 * Session replay is opt-in per org, on top of everything `isEnabled()`
 * requires: the org must also carry `feature:posthog_session_recording`.
 * Recording is unmasked, so only grant this to an org that has consented.
 */
const isSessionRecordingEnabled = () =>
  isEnabled() && hasAnyRole(currentRoles, `feature:${RESOURCE_TYPE.POSTHOG_SESSION_RECORDING}`);

/**
 * Register the `user_role` super property, independent of `identifyUser`'s
 * once-per-session dedupe. Cheap (no hashing), so it's safe to call on every
 * route-guard pass — only actually calls into posthog-js when the role set
 * has changed since the last registration.
 */
const registerRoles = (roles) => {
  if (!roles?.length) {
    return;
  }

  const key = [...roles].sort().join(',');
  if (key === registeredRolesKey) {
    return;
  }

  registeredRolesKey = key;
  posthog.register({ user_role: roles });
};

/**
 * Start PostHog if configured. Safe to call repeatedly — a no-op once
 * `initialized`, and a no-op at app boot for any org until `identifyUser`
 * supplies roles and calls this again, since `isEnabled()` needs those
 * roles to know whether the org is Anthill's own. Tracking is
 * non-critical, so a failing init is swallowed with a warning — analytics
 * must never break app bootstrap.
 */
const startIfEnabled = () => {
  if (initialized || !isEnabled()) {
    return;
  }

  // The SDK is still downloading: retry once it lands (and only if it did —
  // a failed load leaves `posthog` null and tracking permanently inert).
  if (!posthog) {
    posthogReady.then(() => posthog && startIfEnabled());
    return;
  }

  // Decided once, here: PostHog loads its remote config only at init, and
  // recording can't start without it. Safe to decide now because
  // `isEnabled()` already waited for the org's `feature:*` roles to arrive.
  const recordSession = isSessionRecordingEnabled();

  try {
    posthog.init(getToken(), {
      api_host: getHost(),
      defaults: '2026-05-30',

      // Anonymous until login, then the server-issued trackableUserId (see
      // `identifyUser`). Must stay `'identified_only'`, never `'never'` —
      // under that value posthog-js silently ignores `identify`.
      person_profiles: 'identified_only',

      autocapture: {
        element_attribute_ignorelist: [
          'aria-label',
          'title',
          'alt',
          'placeholder',
          'href',
          'value',
          'name',
          'src',
          'srcset',
          'modelvalue',
        ],
      },
      capture_pageview: 'history_change',
      capture_pageleave: true,

      capture_heatmaps: false,
      capture_dead_clicks: false,
      capture_performance: false,
      capture_exceptions: false,

      // Element text is where this app's customer content lives (document
      // titles, prompts, generated copy). Masked at source, since autocapture
      // emits the element twice and only the `$elements` array (handled in
      // sanitizeElements above) can be edited by key.
      mask_all_text: true,

      // Replay records rendered page text, which inside the authoring
      // surfaces IS the client's content, so it is on only for orgs
      // carrying `feature:posthog_session_recording`.
      disable_session_recording: !recordSession,
      // Recording org only: typed input values are captured too. Password
      // fields stay masked regardless (rrweb always masks them).
      session_recording: { maskAllInputs: false },

      disable_surveys: true,
      disable_surveys_automatic_display: true,
      disable_product_tours: true,
      disable_conversations: true,

      // No remote config, no `/flags` request — PostHog cannot change how
      // this SDK behaves without us releasing. The one exception is a
      // recording org: the SDK won't start replay until PostHog's remote
      // config says it's enabled, so the config fetch is allowed for it.
      advanced_disable_flags: !recordSession,
      advanced_disable_decide: !recordSession,
      advanced_disable_feature_flags: true,
      advanced_disable_feature_flags_on_first_load: true,

      // Blocks every PostHog-served remote script (rrweb included) — lifted
      // only for a recording org.
      disable_external_dependency_loading: !recordSession,

      save_campaign_params: false,
      save_referrer: false,
      disable_capture_url_hashes: true,

      before_send: sanitizeEvent,

      // Closes the `?__posthog_debug=true` escape hatch.
      debug: false,
    });

    initialized = true;
    registerGlobalProperties();

    if (pendingAreaView) {
      posthog.capture('area-viewed', { area: pendingAreaView });
      pendingAreaView = null;
    }
  } catch (error) {
    console.warn('[tracking] init failed; continuing without it', error);
  }
};

/** Call once from main.js, before mounting the app. */
export const initializeTracking = () => {
  startIfEnabled();
};

/**
 * Record that something happened. A no-op until tracking has started, so
 * callers never need to check.
 *
 * @param {string} eventName - kebab-case `object-verb-past`, the exact `event_name` of the row in the telemetry event map, a fixed string
 *   literal. Never interpolated — that creates a new event definition per
 *   value and PostHog rate-limits projects with too many.
 * @param {object} [properties] - Low-cardinality descriptors. Anything not
 *   in EVENT_PROPERTIES above is dropped before sending.
 */
export const trackEvent = (eventName, properties) => {
  if (!initialized) {
    return;
  }

  posthog.capture(eventName, properties);
};

/**
 * Record that the user opened a screen (`area-viewed`). Called from the router's
 * `afterEach` with the `meta.area` of the route, so `area` is always a
 * fixed value from the route table, never derived from the URL.
 *
 * The screen goes in `area`, not `surface`: `surface` is the registered global
 * that says which emitter sent an event (`frontend` or `backend`), so it is left
 * alone here.
 *
 * Before tracking has started the view is held and replayed after init, rather
 * than dropped like `trackEvent` would.
 *
 * @param {string} area - A fixed screen name, e.g. `discover`.
 */
export const trackAreaViewed = (area) => {
  if (!area) {
    return;
  }

  if (!initialized) {
    pendingAreaView = area;
    return;
  }

  trackEvent('area-viewed', { area });
};

/**
 * Record that the AI wrote the first draft of a new authoring session
 * (`authoring-draft-generated`). Called by `telemetry/authoringDraftTelemetry.js`,
 * which decides when it is the first draft. No properties yet: the map lists
 * `source` but defines no values for it.
 */
export const trackAuthoringDraftGenerated = () => {
  trackEvent('authoring-draft-generated');
};

/**
 * Record that an AI agent run began (`agent-run-started`). Purpose-built, like
 * `trackAreaViewed`, so `telemetry/agentRunTelemetry.js` — which watches the stream —
 * never calls the untyped `trackEvent` itself.
 *
 * @param {object} run
 * @param {string} run.conversationId - The conversation (graph thread) id, sent as is.
 * @param {string} run.conversationType - A fixed value, e.g. `assist`.
 * @param {string} run.filterApplied - Which kinds of filter the run carried (names only, e.g. `country,product`), or `none`.
 */
export const trackAgentRunStarted = ({ conversationId, conversationType, filterApplied }) => {
  trackEvent('agent-run-started', {
    conversation_id: conversationId,
    conversation_type: conversationType,
    filter_applied: filterApplied,
  });
};

/**
 * Record that an AI agent run finished (`agent-run-completed`), successfully or not.
 *
 * @param {object} run
 * @param {'success'|'error'} run.outcome
 * @param {string} run.durationBucket - A bucket name, never the raw duration.
 * @param {number} run.toolCount - Distinct tool calls the run made.
 * @param {number|null} [run.errorCode] - The HTTP status, when the failure had one.
 */
export const trackAgentRunCompleted = ({ outcome, durationBucket, toolCount, errorCode = null }) => {
  trackEvent('agent-run-completed', {
    outcome,
    duration_bucket: durationBucket,
    tool_count: toolCount,
    ...(errorCode !== null && { error_code: errorCode }),
  });
};

/**
 * Call after login, once the user's email and roles are known. The pseudonym
 * is issued BY THE BACKEND (`GET /v2/telemetry/identity`) so the hashing
 * secret never reaches the browser and the id is byte-identical to the one the
 * backend emits — a flow that starts here and finishes on the server lands on
 * one person. Fails closed: a null response (backend gates off) or any error
 * leaves events anonymous rather than falling back to anything email-derived.
 */
export const identifyUser = async (email, roles) => {
  if (!email) {
    return;
  }

  // `isEnabled()` needs roles to decide if this is Anthill's own org, so stash
  // them before retrying `startIfEnabled()`.
  currentRoles = roles || [];
  startIfEnabled();

  if (!initialized) {
    return;
  }

  registerRoles(roles);

  if (email === identifiedEmail) {
    return;
  }

  const client = getGatewayClient();
  if (!client) {
    // Gateway plugin not installed yet — don't cache; the next route-guard pass
    // retries rather than staying anonymous forever over a timing gap.
    return;
  }

  const generation = identifyGeneration;

  let trackableUserId;
  try {
    const { data } = await client.get(apiGatewayEndpoints.telemetry.identity);
    trackableUserId = data?.trackableUserId ?? null;
  } catch (error) {
    console.warn('[tracking] could not resolve telemetry identity; staying anonymous', error);
    return;
  }

  if (!trackableUserId) {
    return; // backend gates closed → stay anonymous
  }

  if (generation !== identifyGeneration) {
    return; // resetUser ran while fetching — this identity no longer applies
  }

  posthog.identify(trackableUserId);
  identifiedEmail = email;
};

/**
 * Set the `account_id` super property to the active organization's name, so
 * frontend events carry the same account as the backend's. Safe to call
 * before tracking has started: the name is kept and applied on init.
 *
 * @param {string} [name] - The organization's `name`; empty falls back to the subdomain slug.
 */
export const setAccount = (name) => {
  accountName = name || null;

  if (!initialized) {
    return;
  }

  const accountId = accountName ?? getOrgSlug();
  if (accountId) {
    posthog.register({ account_id: accountId });
  } else {
    posthog.unregister('account_id');
  }
};

/** Call on logout, so the next person to use the browser doesn't inherit the last one's identity. */
export const resetUser = () => {
  accountName = null;
  pendingAreaView = null;
  identifyGeneration += 1;
  identifiedEmail = null;
  registeredRolesKey = null;

  if (initialized) {
    posthog.reset();
    // reset() clears all super properties, including the environment-level
    // ones that are still valid for the next session in this browser.
    registerGlobalProperties();
  }
};
